import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { P360Card } from 'legacy/lut/components/widgets/public/partner360/p360Card'
import React, { useMemo } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import {
  AbsolutePopup,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'
import { TOUR_TARGETS } from 'partner360/configs/widgetTour/planningActivitiesTourTargets'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { KeyInitiativesQuickFilter } from 'partner360/features/keyInitiatives/model/types'
import { useKeyInitiativesFiltersForm } from 'partner360/features/keyInitiatives/ui/hooks/useKeyInitiativesFiltersForm'
import { KeyInitiativesControls } from 'partner360/features/keyInitiatives/ui/keyInitiativeControls/KeyInitiativesControls'
import { KeyInitiativesFilters } from 'partner360/features/keyInitiatives/ui/keyInitiativeFilters/KeyInitiativesFilters'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import styles from './ViewSettings.module.scss'

interface IViewSettingsProps {
  programType: ProgramTypes
  isRollupMode: boolean
  data: TKeyInitiative[]
  actualsType: ActualsValueTypes
  onChangeActualsType: (value: ActualsValueTypes) => void
  actualsTypes: ActualsValueTypes[]
}

export const ViewSettings = ({
  programType,
  data,
  isRollupMode,
  onChangeActualsType,
  actualsTypes,
  actualsType,
}: IViewSettingsProps) => {
  const {
    keyObjectives,
    countryCodes,
    statuses,
    businessPriorities,
    quickFilter,
    keyInitiativeIds,
    resetQuickFilter,

    changeKeyObjectives,
    changeBusinessPriorities,
    changeStatuses,
    changeCountryCodes,
    resetFormData,
  } = useKeyInitiativesFiltersForm()

  const appliedFilterBubbles = useMemo(() => {
    return [
      {
        label: TRANSLATION_KEYS.dashboard.filters.strategicInitiativeBubble,
        count: keyObjectives?.length,
        onRemove: () => changeKeyObjectives([]),
      },
      {
        label: TRANSLATION_KEYS.dashboard.filters.businessPriorityBubble,
        count: businessPriorities?.length,
        onRemove: () => changeBusinessPriorities([]),
      },
      {
        label: TRANSLATION_KEYS.dashboard.filters.statusBubble,
        count: statuses?.length,
        onRemove: () => changeStatuses([]),
      },
      {
        label: TRANSLATION_KEYS.dashboard.filters.geoBubble,
        count: countryCodes?.length,
        onRemove: () => changeCountryCodes([]),
      },
    ].filter((item) => Boolean(item.count))
  }, [
    keyObjectives?.length,
    businessPriorities?.length,
    statuses?.length,
    countryCodes?.length,
    changeKeyObjectives,
    changeBusinessPriorities,
    changeStatuses,
    changeCountryCodes,
  ])

  const appliedFiltersCount = appliedFilterBubbles.length

  const clearAllFilters = () => {
    resetFormData()
  }

  const quickFilterMsg = useMemo(() => {
    switch (quickFilter) {
      case KeyInitiativesQuickFilter.WithoutFinancialTargets:
        return TRANSLATION_KEYS.quality.quickFilter.withoutFinancialTargets

      case KeyInitiativesQuickFilter.LowDescriptionQuality:
        return TRANSLATION_KEYS.quality.quickFilter.lowDescriptionQuality

      case KeyInitiativesQuickFilter.WithoutBusinessPriorities:
        return TRANSLATION_KEYS.quality.quickFilter.withoutBusinessPriorities

      default:
        return undefined
    }
  }, [quickFilter])

  return (
    <P360Card className={styles.root}>
      <Flex flexDirection={'column'}>
        <RenderWhen condition={Boolean(quickFilter)}>
          <div className={styles.quickFilterMsgBody}>
            {quickFilterMsg && (
              <Translation
                text={quickFilterMsg}
                replaceParams={[{ value: String(keyInitiativeIds?.length || '-'), key: '{count}' }]}
              />
            )}

            <Button variant={'text'} color={'grey'} onClick={resetQuickFilter}>
              <Translation text={TRANSLATION_KEYS.dashboard.filters.resetFilters} />
            </Button>
          </div>
        </RenderWhen>

        <div className={styles.toolbar} data-tour-id={TOUR_TARGETS.filters}>
          <div className={styles.filtersSide}>
            <AbsolutePopup
              rootClassName={styles.popupRoot}
              horizontalDirection={TPopupHorizontal.Left}
              verticalDirection={TPopupVertical.Bottom}
              horizontalStartPosition={TPopupHorizontal.Right}
              verticalStartPosition={TPopupVertical.Bottom}
              visibilityTrigger={TVisibilityTrigger.Click}
              popupContent={({ action }) => (
                <KeyInitiativesFilters
                  data={data}
                  onClose={action.close}
                  programType={programType}
                  isRollupMode={isRollupMode}
                />
              )}
            >
              <Button
                variant={'outlined'}
                color={'grey'}
                disabled={Boolean(quickFilter)}
                className={styles.btn}
              >
                <Flex alignItems={'center'}>
                  <i className={'kbase kbase-nakb-menu'} />
                  <Translation text={TRANSLATION_KEYS.dashboard.filters.title} />
                  {appliedFiltersCount ? (
                    <div className={styles.filterCount}>{appliedFiltersCount}</div>
                  ) : null}
                </Flex>
              </Button>
            </AbsolutePopup>

            {appliedFilterBubbles.map(({ label, count, onRemove }) => (
              <button
                key={label}
                type={'button'}
                className={styles.filterBubble}
                onClick={onRemove}
                disabled={Boolean(quickFilter)}
              >
                <span>
                  <Translation
                    text={label}
                    replaceParams={[{ key: '{count}', value: String(count) }]}
                  />
                </span>
                <i className={cn(styles.filterBubbleClose, 'kbase kbase-close')} />
              </button>
            ))}

            <RenderWhen condition={Boolean(appliedFilterBubbles.length)}>
              <Button
                variant={'text'}
                color={'grey'}
                size={'sm'}
                onClick={clearAllFilters}
                disabled={Boolean(quickFilter)}
              >
                <Translation text={TRANSLATION_KEYS.dashboard.filters.resetBubble} />
              </Button>
            </RenderWhen>
          </div>

          <div className={styles.controlsSide}>
            <KeyInitiativesControls
              onChangeActualsType={onChangeActualsType}
              actualsType={actualsType}
              actualsTypes={actualsTypes}
            />
          </div>
        </div>
      </Flex>
    </P360Card>
  )
}
